<template>

  <div class="audio-pane-container">

    <div 
      v-if    = "showMeters"
      class   = "meters-container"
    >

      <div class="meters pa-4">

        <div class="legend">

          <div class="label">Peak</div>
          <div class="label">Avg</div>

          <div class="ticks">
          
            <div 
              v-for   = "tick in ticks"  
              class   = "tick"
              :key    = "tick"
            >
              <div class="label">{{ tick }}</div>
            </div>
          </div>

          <div class="label">dB</div>

        </div>

        <AudioMeter
          v-for      = "(meter, index) in audio.meters"
          :key       = "index"
          :meter     = "meter"
        >
        </AudioMeter>
      
      </div>

      <v-toolbar 
        dense 
        class       = "flex-grow-0"
        elevation   = "0" 
        color       = "rgba(0, 0, 0, 0.3)"
      >

        <v-menu
          right
          offset-y
          top
        >

          <template v-slot:activator="{ on }">
            <v-btn
              icon
              dark
              v-on    = "on"
            >
              <v-icon>mdi-cog</v-icon>
            </v-btn>
          </template>
          
          <div>
            <div class="peak-title">
              Peak Hold
            </div>
            <v-divider></v-divider>
            <v-list-item-group
              mandatory
              v-model    = "currentPeakMode"
              @change    = "onPeakChange"
            >

              <v-list-item
                v-for   = "(peak, index) in peakModes"
                color   = "primary"
                :key    = "index"
                :value  = "peak"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ peak.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

            </v-list-item-group>
          </div>

          
        </v-menu>
        <!-- Previous Page -->
        <v-btn
          v-if        = "needsPages"
          icon
          :disabled   = "!canGoToPreviousPage"
          @click      = "onPreviousPage"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <!-- Currently Showing -->
        <span 
          class   = "page-range"
          v-if    = "needsPages"
        >
          {{ firstChannelOnPage }} - {{ lastChannelOnPage }}
        </span>

        <!-- Next Page -->
        <v-btn
          v-if        = "needsPages"
          icon
          :disabled   = "!canGoToNextPage"
          @click      = "onNextPage"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>

        <v-spacer></v-spacer>

        <!-- Audio Tracks -->
        <v-select
          v-if        = "hasAudioTrack"
          v-model     = "selectedAudioTrack"
          class       = "tracks elevation-0"
          :items      = "audioTracks"
          item-value  = "index"
          item-text   = "name"
          single-line
          hide-details
          dense
          solo
        >
        </v-select>

        <ToolbarButton
          tooltip   = "Add Audio Track"
          @click    = "onAddAudioTrack"
        >
          <v-icon>mdi-plus</v-icon>
        </ToolbarButton>

        <v-divider
          v-if        = "hasAudioTrack"
          vertical
          class       = "mx-2"
        >
        </v-divider>

        <!-- Left Channel -->
        <v-select
          v-if          = "hasAudioTrack"
          class         = "channels elevation-0"
          v-model       = "selectedLeftChannel"
          :items        = "audioChannels"
          :disabled     = "outputIsSurround"
          item-value    = "index"
          item-text     = "name"
          single-line
          hide-details
          dense
          solo
        >
        </v-select>

        <v-icon 
          v-if      = "hasAudioTrack"
          class     = "left-speaker mr-2"
          :disabled = "outputIsSurround"
        >
          mdi-volume-medium
        </v-icon>
        <v-icon
          v-if      = "hasAudioTrack"
          :disabled = "outputIsSurround"
        >
          mdi-volume-medium
        </v-icon>

        <!-- Right Channel -->
        <v-select
          v-if          = "hasAudioTrack"
          class         = "channels elevation-0"
          :class        = "{ 'mr-2': hasSurround }"
          v-model       = "selectedRightChannel"
          :items        = "audioChannels"
          :disabled     = "outputIsSurround"
          item-value    = "index"
          item-text     = "name"
          single-line
          hide-details
          dense
          solo
        >
        </v-select>

        <ToolbarButton
          v-if      = "hasSurround"
          :tooltip  = "outputIsSurround ? 'Disable Surround Output' : 'Enable Surround Output'"
          :color    = "outputIsSurround ? 'primary' : 'default'"
          @click    = "onToggleSurround"
        >
          <v-icon>mdi-surround-sound</v-icon>
        </ToolbarButton>

      </v-toolbar>

    </div>

    <Wizard 
      v-if    = "showNoAudioWizard" 
      class   = "wizard" 
      icon    = "mdi-volume-off"
    >
      <div>
        <span class="text-h5">Media does not contain audio.</span>
        <v-divider class="my-2"></v-divider>
        <div class="text-right">
          <v-btn
            color     = "primary"
            text
            small
            @click    = "onAddAudioTrack"
          >
            Add Audio
          </v-btn>
        </div>
      </div>
    </Wizard>

    <FileBrowser 
      ref     = "filebrowser"
      @folder = "lastBrowsedFolder = $event"
    >
    </FileBrowser>

  </div>

</template>

<script lang="js">

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import AudioMeter from './components/AudioMeter';
import FileBrowser from './components/FileBrowser';
import ToolbarButton from './components/buttons/ToolbarButton';
import Wizard from './components/Wizard';
import { SET_AUDIO_METERS_OFFSET, SET_PEAK_MODE} from '@/utils/mutation-types';
import { 
  LOAD_VIDEO, 
  MODIFY_AUDIO_SOUND_FIELD, 
  SELECT_AUDIO_TRACK 
} from '@/utils/action-types';
import {clearMeters} from "@/store/audio.store";

// #endregion Imports

export default {

  // #region Properties

  name: 'AudioPane',
  data() {
    return {
      ticks:     [0, -6, -12, -18, -24, -30, -36, -42, -48, -54, -60, -66, -72],
      peakModes: [ 
        {mode: 'default', title: 'No Hold'},
        {mode: 'fourSeconds', title: '4s Hold'},
        {mode: 'infinite', title: 'Infinite Hold'}
      ],
      currentPeakMode:   {mode: 'default', title: 'No Hold'},
      lastBrowsedFolder: undefined
    }
  },
  computed: {
    ...mapState([
      'audio',
      'video',
      'videoController',
      'videoPath'
    ]),
    showMeters() {
      return this.audio.meters.filter(m => m.enabled).length > 0;
    },
    showNoAudioWizard() {
      const properties = this.video.properties;
      return properties && properties.audioProperties.length === 0;
    },
    needsPages() {
      return this.audio.channelCount > this.audio.meters.length;
    },
    firstChannelOnPage() {
      return (this.audio.metersOffset + 1);
    },
    lastChannelOnPage() {
      let remainder = this.audio.channelCount - this.audio.metersOffset;
      if (remainder > this.audio.meters.length) {
        remainder = this.audio.meters.length
      }
      return (this.audio.metersOffset + remainder);
    },
    canGoToPreviousPage() {
      return this.audio.metersOffset > 0;
    },
    canGoToNextPage() {
      return this.lastChannelOnPage < this.audio.channelCount;
    },
    audioTracks() {
      if (
        !this.videoController
      ) {
        return [];
      }

      return this.videoController.audioTracksList
        .map(t => ({ 
          index: t.track,
          name:  t.description ? t.description : `Track ${t.track + 1}`
        }));
    },
    selectedAudioTrack: {
      get() {
        if (
          !this.videoController ||
          !this.videoController.currentAudioTrack
        ) {
          return undefined;
        }

        return this.videoController.currentAudioTrack.track;
      },
      set(trackIndex) {
        this.$store.dispatch(SELECT_AUDIO_TRACK, trackIndex);
        if (this.videoController.currentAudioTrack.soundfields.has('Surround')) {
          this.$store.dispatch(
            MODIFY_AUDIO_SOUND_FIELD,
            {
              soundField: 'Surround'
            }
          );
        }
      }
    },
    hasAudioTrack() {
      return this.selectedAudioTrack !== undefined;
    },
    hasSurround() {
      if (!this.hasAudioTrack) {
        return false;
      }

      return this.videoController.currentAudioTrack.soundfields.has('Surround');
    },
    outputIsSurround() {
      if (!this.hasAudioTrack) {
        return false;
      }

      return this.videoController.currentAudioTrack.currentSoundfield === 'Surround';
    },
    audioChannels() {
      if (!this.hasAudioTrack) {
        return [];
      }
      
      const track = this.videoController.currentAudioTrack;
      const labels = track.channelLabels || [];
      const mappedLabels = track.mappedChannelLabels || [];

      return labels
        .map((l, i) => ({
          index: i,
          name:  l || mappedLabels[i] || `${i + 1}`
        }));
    },
    selectedLeftChannel: {
      get() {
        return this.videoController?.currentAudioTrack.left;
      },
      set(channelIndex) {
        this.$store.dispatch(
          MODIFY_AUDIO_SOUND_FIELD,
          {
            soundField: 'Stereo',
            left:       channelIndex,
            right:      this.selectedRightChannel
          }
        );
      }
    },
    selectedRightChannel: {
      get() {
        return this.videoController?.currentAudioTrack.right;
      },
      set(channelIndex) {
        this.$store.dispatch(
          MODIFY_AUDIO_SOUND_FIELD,
          {
            soundField: 'Stereo',
            left:       this.selectedLeftChannel,
            right:      channelIndex
          }
        );
      }
    }
  },
  components: {
    AudioMeter,
    FileBrowser,
    ToolbarButton,
    Wizard
  },

  // #endregion Properties

  // #region Operations

  methods: {

    onPreviousPage() {
      const newOffset = this.firstChannelOnPage - this.audio.meters.length - 1;
      this.$store.commit(SET_AUDIO_METERS_OFFSET, newOffset);
    },

    onNextPage() {
      const newOffset = this.firstChannelOnPage + this.audio.meters.length - 1;
      this.$store.commit(SET_AUDIO_METERS_OFFSET, newOffset);
    },

    async onAddAudioTrack() {
      const files = await this.$refs.filebrowser.select('Add Audio Track', this.lastBrowsedFolder);

      const manifestUrlParams = new URLSearchParams(new URL(this.videoController.masterManifestUrl).search);
      const currentAudioPaths = manifestUrlParams.getAll('sidecarAudioPaths');
      const audioPaths = [...new Set([...currentAudioPaths, ...files])];
      
      this.$store.dispatch(
        LOAD_VIDEO,
        {
          videoPath:     this.videoPath,
          audioPaths:    audioPaths,
          subtitlePaths: this.videoController.sidecarSubtitles.map(s => s.path)
        }
      );
    },

    onToggleSurround() {
      this.$store.dispatch(
        MODIFY_AUDIO_SOUND_FIELD,
        {
          soundField: this.outputIsSurround ? 'Stereo' : 'Surround',
          left:       this.videoController.currentAudioTrack.left,
          right:      this.videoController.currentAudioTrack.right
        }
      );
    },

    onPeakChange(value){
      clearMeters();
      this.$store.commit(SET_PEAK_MODE, value.mode)
    }

  }

  // #endregion Operations

};

</script>

<style lang="scss" scoped>

@import "../../styles/colors.scss";

.audio-pane-container,
.meters-container,
.wizard {
  height: 100%;
}

.v-menu__content {
  background-color: #1E1E1E !important;
}

.legend {
  display:          flex;
  flex-direction:   column;

  .label {
    height:         20px; 
    text-align:     right;

    font-size:      12px;
    color:          $label-color;
  }

  .ticks {
    flex:             1;
    display:          flex;
    flex-direction:   column;
    justify-content:  space-between;
    margin:           8px 0px;

    .tick {
      height:           1px;

      .label {
        text-align:       right;
        margin-top:       -7px;

        font-size:        10px;
        color:            $label-color;
      }
    }
  }
}

.meters-container {
  display:        flex;
  flex-direction: column;

  .page-range {
    color:          $label-color;
  }

  .meters {
    flex:             1;

    display:          flex;
    flex-direction:   row;
    justify-content:  space-between;
  }
}

.v-toolbar {
  .tracks {
    width:        160px;
    max-width:    160px;
  }

  .left-speaker {
    transform:    scaleX(-1);
  }

  .channels {
    width:        85px;
    max-width:    85px;
  }
}

.peak-title{
  margin-left: 15px;
  margin-top: 10px;
  margin-bottom: 10px;
}

</style>